/** Access group management.
 *
 * Works with OR without a selected tenant:
 *  - no tenant → a cross-tenant paginated flat list (`/access_groups/`) with a
 *    tenant column;
 *  - tenant selected → the parent/child tree (client-side DataGridPro treeData).
 * Groups are deep-linkable at `/rbac/access-groups/:id` (tenant resolved
 * server-side). Managed groups (template-controlled) are flagged; their
 * settings/permissions are edited via the template.
 */
import AddIcon from '@mui/icons-material/Add';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  getGridStringOperators,
  type GridColDef,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import {
  accessGroupsApi,
  type AccessGroupGlobalRow,
  type AccessGroupRow,
} from '@/api/accessGroups';
import { ConsoleApiError } from '@/api/client';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CellActionsPopover } from '@/components/DataGrid/CellActionsPopover';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { PageHeader } from '@/components/PageHeader';
import { PermissionGate } from '@/components/PermissionGate';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { boundToParam, paramToBound, TimeWindowPicker } from '@/components/TimeWindowPicker';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import {
  accessGroupActions,
  managedGroupsActions,
  rbacOperatorActions,
} from '@/features/users/userLinks';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { usePermissions } from '@/hooks/usePermissions';
import { fmtTs, relativeTime, utcTs } from '@/features/bgtasks/taskFormat';
import { useScopedTenantState } from '@/scope/useScopedTenantState';

import { AccessGroupEditDialog } from './AccessGroupEditDialog';

type Row = AccessGroupRow & { __path: string[] };

const containsOnly = getGridStringOperators().filter((op) => op.value === 'contains');

const boolStr = (v: string) => (v === 'true' || v === 'false' ? v === 'true' : undefined);


/** Global-grid filters (URL-backed): the Access-group name column filter, the
 *  Created date range (set from the Created cell's ± presets) and the Managed
 *  select (promoted from local state so presets/deep links can carry it). */
const GLOBAL_FILTERS: FilterDef[] = [
  { param: 'name_contains', column: { field: 'name', operator: 'contains' } },
  { param: 'created_after' },
  { param: 'created_before' },
  { param: 'managed', toApi: boolStr },
];

/** Presets store the global-grid filter state as `{params: {...}}`; the tree
 *  view (tenant selected) is out of preset scope entirely. */
const PRESET_FILTERS = makeResourceFiltersAdapter(GLOBAL_FILTERS);

/** Global grid column field order (mirrors globalColumns — keep in sync). */
const FIELD_ORDER = [
  'name',
  'tour_operator',
  'codename',
  'member_count',
  'managed',
  'full_access',
  'roles',
  'template',
  'created_ts',
  'updated_ts',
];

/** Supplementary columns hidden by default (available via the columns menu). */
const GLOBAL_HIDDEN = ['roles', 'template', 'updated_ts'];

const CREATED_DELTAS: { label: string; tooltip: string; ms: number }[] = [
  { label: '±1h', tooltip: '±1 hour', ms: 3_600_000 },
  { label: '±1d', tooltip: '±1 day', ms: 24 * 3_600_000 },
  { label: '±7d', tooltip: '±7 days', ms: 7 * 24 * 3_600_000 },
  { label: '±30d', tooltip: '±30 days', ms: 30 * 24 * 3_600_000 },
];

/** Parent-chain tree path; ids appended only on name collisions. Deduped paths
 *  are cached so children inherit their parent's deduped prefix. */
function buildTreePaths(groups: AccessGroupRow[]): Row[] {
  const byId = new Map(groups.map((g) => [g.id, g]));
  const paths = new Map<number, string[]>();
  const taken = new Set<string>();
  const pathOf = (g: AccessGroupRow): string[] => {
    const cached = paths.get(g.id);
    if (cached) return cached;
    const parent = g.parent !== null ? byId.get(g.parent) : undefined;
    const prefix = parent ? pathOf(parent) : [];
    let path = [...prefix, g.name];
    if (taken.has(path.join(' '))) path = [...prefix, `${g.name} (${g.id})`];
    taken.add(path.join(' '));
    paths.set(g.id, path);
    return path;
  };
  return groups.map((g) => ({ ...g, __path: pathOf(g) }));
}

export function AccessGroupsListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_rbac_manage');
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id?: string }>();
  const deepLinkId = idParam && /^\d+$/.test(idParam) ? Number(idParam) : null;

  const [top, setTop] = useScopedTenantState();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<AccessGroupRow | null>(null);

  const presets = useGridPresets({
    grid: 'access_groups',
    defaultOrder: FIELD_ORDER,
    defaultHidden: GLOBAL_HIDDEN,
    defaultOrdering: 'tour_operator',
    filters: PRESET_FILTERS,
  });

  // Global (flat) grid column/date/managed filters — URL-backed via the shared
  // hook (+ the preset layer). The `search` box stays local (quick-search is
  // never preset-covered; it also drives the tenant tree).
  const gf = useGridFilters(GLOBAL_FILTERS, presets.filtersLayer);
  const managedRaw = gf.getParam('managed');
  const managedFilter: 'all' | 'true' | 'false' =
    managedRaw === 'true' || managedRaw === 'false' ? managedRaw : 'all';
  const setManagedFilter = (v: 'all' | 'true' | 'false') =>
    gf.setParam('managed', v === 'all' ? null : v);

  // Tenant tree (only when a tenant is selected). The flat global grid is a
  // ResourceDataGrid with its own Refresh control.
  const treeAutoRefresh = useGridAutoRefresh({ grid: 'access_groups.tree' });
  const treeQuery = useQuery({
    queryKey: ['accessGroups', top?.id, search, managedFilter],
    queryFn: () =>
      accessGroupsApi.list(top!.id, {
        search,
        managed: managedFilter === 'all' ? undefined : managedFilter === 'true',
      }),
    enabled: top !== null,
    ...treeAutoRefresh.queryOptions(),
  });
  const rows = useMemo(() => buildTreePaths(treeQuery.data?.results ?? []), [treeQuery.data]);

  // Deep link → resolve the group's tenant server-side, then open the editor.
  const deepLinkQuery = useQuery({
    queryKey: ['accessGroupById', deepLinkId],
    queryFn: () => accessGroupsApi.retrieveById(deepLinkId!),
    enabled: deepLinkId !== null,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ topId, id }: { topId: number; id: number }) => accessGroupsApi.remove(topId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accessGroups'] });
      qc.invalidateQueries({ queryKey: ['accessGroupsGlobal'] });
      snackbar.show('Access group deleted', 'success');
    },
    onError: (e) => {
      const msg =
        e instanceof ConsoleApiError && (e.status === 409 || e.status === 406)
          ? ((e.body as { message?: string } | undefined)?.message ?? e.message)
          : (e as Error).message;
      snackbar.show(msg, 'error');
    },
  });

  const treeColumns = useMemo<GridColDef<Row>[]>(
    () => [
      {
        field: 'codename',
        headerName: 'Code name',
        width: 160,
        valueGetter: (value?: string | null) => value ?? '',
        renderCell: (p) =>
          p.row.codename ? (
            <ValueActions actions={[{ copy: p.row.codename, label: 'Copy code name' }]}>
              {p.row.codename}
            </ValueActions>
          ) : (
            '—'
          ),
      },
      {
        field: 'member_count',
        headerName: 'Members',
        width: 90,
        // There is no `agroup` filter on the users grids; the members are
        // reachable through the bookings they own.
        renderCell: (p) =>
          typeof p.row.id === 'number' ? (
            <ValueActions actions={accessGroupActions(p.row, top?.id ?? null)}>
              {p.row.member_count}
            </ValueActions>
          ) : (
            ''
          ),
      },
      {
        field: 'managed',
        headerName: 'Managed',
        width: 130,
        renderCell: (p) =>
          p.row.managed ? (
            <ValueActions chip actions={managedGroupsActions()}>
              <Chip size="small" color="primary" label="managed" />
            </ValueActions>
          ) : p.row.is_guest_group ? (
            <Chip size="small" variant="outlined" label="guest group" />
          ) : null,
      },
      {
        field: 'template',
        headerName: 'Template',
        width: 180,
        sortable: false,
        valueGetter: (_, row) => row.template?.name ?? '',
      },
      {
        field: 'roles',
        headerName: 'Roles',
        width: 160,
        sortable: false,
        valueGetter: (_, row) => row.roles?.map((r) => r.name).join(', ') ?? '',
      },
      {
        field: 'atag',
        headerName: 'Tags',
        flex: 1,
        sortable: false,
        valueGetter: (_, row) => row.atag?.map((t) => t.name).join(', ') ?? '',
      },
      {
        field: 'created_ts',
        headerName: 'Created',
        width: 160,
        valueFormatter: (value?: string | null) => (value ? new Date(value).toLocaleString() : ''),
      },
      {
        field: '__actions__',
        headerName: '',
        width: 90,
        sortable: false,
        renderCell: (p) =>
          canManage && typeof p.row.id === 'number' && !p.row.managed && !p.row.is_guest_group ? (
            <Button
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setDeleting(p.row);
              }}
            >
              Delete
            </Button>
          ) : null,
      },
    ],
    [canManage, top],
  );

  const globalColumns = useMemo<GridColDef<AccessGroupGlobalRow>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Access group',
        flex: 1.2,
        filterOperators: containsOnly,
        renderCell: (p) => (
          // The inline link keeps the row's own click (same tab); the popover
          // adds the bookings of this group's members.
          <ValueActions actions={accessGroupActions(p.row, p.row.tour_operator.id)}>
            <RouterLink to={`/rbac/access-groups/${p.row.id}`} style={{ color: '#0a1a3f' }}>
              {p.row.name}
            </RouterLink>
          </ValueActions>
        ),
      },
      {
        field: 'tour_operator',
        headerName: 'Tenant',
        flex: 1,
        sortable: true,
        filterable: false,
        renderCell: (p) => (
          <WithTenantMark environment={p.row.tour_operator.environment}>
            <ValueActions actions={rbacOperatorActions(p.row.tour_operator)}>
              <RouterLink to={`/tour-operators/${p.row.tour_operator.id}`} style={{ color: '#0a1a3f' }}>
                {p.row.tour_operator.code
                  ? `${p.row.tour_operator.name} (${p.row.tour_operator.code})`
                  : p.row.tour_operator.name}
              </RouterLink>
            </ValueActions>
          </WithTenantMark>
        ),
      },
      {
        field: 'codename',
        headerName: 'Code name',
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          p.row.codename ? (
            <ValueActions actions={[{ copy: p.row.codename, label: 'Copy code name' }]}>
              {p.row.codename}
            </ValueActions>
          ) : (
            '—'
          ),
      },
      {
        field: 'member_count',
        headerName: 'Members',
        width: 90,
        filterable: false,
        renderCell: (p) => (
          <ValueActions actions={accessGroupActions(p.row, p.row.tour_operator.id)}>
            {p.row.member_count}
          </ValueActions>
        ),
      },
      {
        field: 'managed',
        headerName: 'Managed',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          p.row.managed ? (
            <ValueActions chip actions={managedGroupsActions()}>
              <Chip size="small" color="primary" label="managed" />
            </ValueActions>
          ) : p.row.is_guest_group ? (
            <Chip size="small" variant="outlined" label="guest group" />
          ) : null,
      },
      {
        field: 'full_access',
        headerName: 'Full access',
        width: 120,
        sortable: false,
        filterable: false,
        // Security-critical: members bypass all access checks. Surface it so ops can
        // scan which groups have it across tenants (RBAC scope-wipe / cache-empty
        // incident forensics).
        renderCell: (p) =>
          p.row.full_access ? <Chip size="small" color="warning" label="full access" /> : null,
      },
      {
        field: 'roles',
        headerName: 'Roles',
        flex: 1,
        // Computed (not in the backend ordering allowlist) — display-only.
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => row.roles?.map((r) => r.name).join(', ') ?? '',
        renderCell: (p) =>
          p.row.roles?.length ? (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {p.row.roles.map((r) => (
                <Chip key={r.id} size="small" variant="outlined" label={r.name} />
              ))}
            </Stack>
          ) : null,
      },
      {
        field: 'template',
        headerName: 'Template',
        width: 180,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => row.template?.name ?? '',
        renderCell: (p) => p.row.template?.name ?? '—',
      },
      {
        field: 'created_ts',
        headerName: 'Created',
        width: 200,
        filterable: false,
        renderCell: (p) => {
          if (!p.row.created_ts) return '—';
          const ts = new Date(p.row.created_ts).getTime();
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.4 }}>
              <span>
                <CellActionsPopover
                  auto
                  title={utcTs(p.row.created_ts)}
                  actions={[
                    ...CREATED_DELTAS.map(({ label, tooltip, ms }) => ({
                      key: label,
                      label,
                      tooltip: `Filter Created to ${tooltip} around this timestamp`,
                      onClick: () =>
                        gf.setParams({
                          created_after: new Date(ts - ms).toISOString(),
                          created_before: new Date(ts + ms).toISOString(),
                        }),
                    })),
                    {
                      key: 'reset',
                      label: 'Reset Created filter',
                      icon: <FilterAltOffIcon fontSize="small" />,
                      onClick: () => gf.setParams({ created_after: null, created_before: null }),
                    },
                  ]}
                >
                  {fmtTs(p.row.created_ts)}
                </CellActionsPopover>
              </span>
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.4 }}
              >
                {relativeTime(p.row.created_ts)}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 160,
        // Not in the backend ordering allowlist — display-only.
        sortable: false,
        filterable: false,
        renderCell: (p) => fmtTs(p.row.updated_ts),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gf.setParams],
  );

  function globalQueryFn(params: ResourceDataGridParams) {
    return accessGroupsApi.globalList({
      limit: params.pageSize,
      offset: (params.page - 1) * params.pageSize,
      ordering: params.ordering ?? presets.ordering,
      search: search || undefined,
      // `managed` arrives resolved in params.extra (URL-backed FilterDef).
      ...(params.extra as Record<string, string>),
    });
  }

  // Which group is being edited: create (local) or a deep-linked existing group.
  const editTopId = creating ? (top?.id ?? null) : (deepLinkQuery.data?.tour_operator ?? null);
  const editGroupId = creating ? null : deepLinkId;
  const editorOpen = creating || (deepLinkId !== null && editTopId !== null);

  const closeEditor = () => {
    if (creating) setCreating(false);
    else navigate('/rbac/access-groups');
  };

  return (
    <>
      <PageHeader
        helpStory="rbac-model"
        title="Access Groups"
        subtitle="Tenant access groups; managed groups mirror their template and are edited through it."
        actions={
          <PermissionGate perm="console_rbac_manage">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={top === null}
              onClick={() => setCreating(true)}
            >
              New group
            </Button>
          </PermissionGate>
        }
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <div style={{ minWidth: 320 }}>
          <TenantPicker
            label="Tour operator"
            placeholder="All tenants (or pick one for the tree view)…"
            value={top}
            onChange={setTop}
          />
        </div>
        <TextField
          size="small"
          select
          label="Managed"
          value={managedFilter}
          onChange={(e) => setManagedFilter(e.target.value as typeof managedFilter)}
          sx={{ width: 140 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="true">Managed</MenuItem>
          <MenuItem value="false">Unmanaged</MenuItem>
        </TextField>
        {/* Created filters only the flat global grid (tree view ignores them). */}
        {top === null && (
          <TimeWindowPicker
            label="Created"
            allowOpenEnded
            value={[
              paramToBound(gf.getParam('created_after')),
              paramToBound(gf.getParam('created_before'), { end: true }),
            ]}
            onChange={([from, to]) =>
              gf.setParams({ created_after: boundToParam(from), created_before: boundToParam(to) })
            }
          />
        )}
      </Stack>

      {top === null ? (
        presets.blocking ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ResourceDataGrid<AccessGroupGlobalRow>
            queryKey={['accessGroupsGlobal', search, managedFilter, gf.extra]}
            queryFn={globalQueryFn}
            columns={orderColumns(globalColumns, presets.orderedFields)}
            extra={gf.extra}
            filterModel={gf.filterModel}
            onFilterModelChange={gf.onFilterModelChange}
            sortModel={presets.sortModel}
            onSortModelChange={presets.onSortModelChange}
            columnVisibilityModel={presets.columnVisibilityModel}
            onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
            onColumnOrderChange={presets.onColumnOrderChange}
            onRowClick={(id) => navigate(`/rbac/access-groups/${id}`)}
            headerToolbar={{
              // External-mode search (shared with the tree view) lives on the
              // page; the name/created column filters live in the URL via gf.
              search: true,
              searchPlaceholder: 'Search access groups',
              searchValue: search,
              onSearchChange: setSearch,
              onResetAllFilters: () => {
                setSearch('');
                setManagedFilter('all');
                gf.resetAll();
              },
              resetDisabled: !search && managedFilter === 'all' && !gf.anyActive,
              exportFileName: 'access-groups',
              presets: presets.toolbar,
            }}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        )
      ) : (
        <DataGridPro<Row>
          treeData
          rows={rows}
          columns={treeColumns}
          getTreeDataPath={(row) => row.__path}
          groupingColDef={{ headerName: 'Access group', flex: 1.4 }}
          defaultGroupingExpansionDepth={-1}
          loading={treeQuery.isFetching}
          sx={{ flex: 1, minHeight: 360 }}
          hideFooter
          // Client-side tree: no server column filtering — the search box
          // filters it. Suppress the (non-functional) column filter menus.
          disableColumnFilter
          slots={{ toolbar: GridControlsToolbar as GridSlots['toolbar'] }}
          slotProps={{
            toolbar: {
              refresh: gridRefreshToolbar(treeQuery, treeAutoRefresh),
              search: true,
              searchPlaceholder: 'Search access groups',
              searchValue: search,
              onSearchChange: setSearch,
              hideFilterButton: true,
              exportFileName: 'access-groups',
            } as GridToolbarProps,
            // A slim top bar, not the blocking overlay, on refetches — auto-refresh
            // would otherwise strobe the whole tree every tick.
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
          }}
          onRowClick={(p) => {
            const row = p.row as Row;
            if (typeof row.id === 'number') navigate(`/rbac/access-groups/${row.id}`);
          }}
        />
      )}

      {editorOpen && editTopId !== null && (
        <AccessGroupEditDialog topId={editTopId} groupId={editGroupId} onClose={closeEditor} />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete access group?"
        message={`Delete "${deleting?.name ?? ''}"? Members are not deleted; their access via this group is revoked.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (deleting && top) deleteMutation.mutate({ topId: top.id, id: deleting.id });
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
